// ============================================================
// CineSync WebRTC Service
// Manages one RTCPeerConnection per remote participant.
// The host creates one connection per guest.
// Guests create one connection to the host.
// ============================================================

import {
  ICE_SERVERS_STUN_ONLY,
  RTC_CONFIG_BASE,
  VIDEO_SENDER_PARAMS,
  VIDEO_QUALITY_PRESETS,
  type VideoQuality,
} from '@/config/webrtc';
import type { StreamStats } from '@/types';
import { computeQualityLabel } from '@/lib/utils';

export type PeerRole = 'offerer' | 'answerer';

interface PeerConnectionOptions {
  peerId: string;
  role: PeerRole;
  onTrack?: (streams: readonly MediaStream[]) => void;
  onDataChannel?: (channel: RTCDataChannel) => void;
  onIceCandidate?: (candidate: RTCIceCandidate) => void;
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
  onNegotiationNeeded?: () => void;
}

/** Wraps a single RTCPeerConnection with helpers */
export class PeerConnection {
  readonly peerId: string;
  private pc: RTCPeerConnection;
  private role: PeerRole;
  private opts: PeerConnectionOptions;
  private statsTimer: ReturnType<typeof setInterval> | null = null;
  private lastBytesSent = 0;
  private lastBytesReceived = 0;
  private lastStatsTime = 0;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private remoteStream: MediaStream | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private isMakingOffer = false;

  constructor(opts: PeerConnectionOptions) {
    this.peerId = opts.peerId;
    this.role = opts.role;
    this.opts = opts;

    this.pc = new RTCPeerConnection({
      ...RTC_CONFIG_BASE,
      iceServers: ICE_SERVERS_STUN_ONLY,
    });

    // Host (offerer) establishes the high-speed YouTube-style RAM transfer DataChannel
    if (this.role === 'offerer') {
      try {
        this.dataChannel = this.pc.createDataChannel('cinesync-transfer', {
          ordered: true,
        });
        opts.onDataChannel?.(this.dataChannel);
      } catch (e) {
        console.warn('[WebRTC] Error creating transfer data channel:', e);
      }
    } else {
      // Guest (answerer) receives the transfer channel from host
      this.pc.ondatachannel = (ev) => {
        if (ev.channel.label === 'cinesync-transfer') {
          this.dataChannel = ev.channel;
          opts.onDataChannel?.(ev.channel);
        }
      };
    }

    this.pc.ontrack = (ev) => {
      // Configure zero-delay playout on incoming video receivers for instant initial playback
      try {
        const receivers = this.pc.getReceivers();
        receivers.forEach((receiver) => {
          if (receiver.track?.kind === 'video' && 'playoutDelayHint' in receiver) {
            (receiver as any).playoutDelayHint = 0; // Immediate playout with zero artificial lag
          }
        });
      } catch {}

      if (ev.streams && ev.streams[0]) {
        opts.onTrack?.(ev.streams);
      } else if (ev.track) {
        if (!this.remoteStream) {
          this.remoteStream = new MediaStream();
        }
        this.remoteStream.addTrack(ev.track);
        opts.onTrack?.([this.remoteStream]);
      }
    };

    this.pc.onicecandidate = (ev) => {
      if (ev.candidate) opts.onIceCandidate?.(ev.candidate);
    };
    this.pc.onconnectionstatechange = () => {
      opts.onConnectionStateChange?.(this.pc.connectionState);
    };
    this.pc.onnegotiationneeded = () => {
      if (this.role === 'offerer') opts.onNegotiationNeeded?.();
    };
  }

  /** Add all tracks from a MediaStream (host → guest) */
  addTracks(stream: MediaStream, quality: VideoQuality = '1080p'): void {
    const existingSenders = this.pc.getSenders();
    stream.getTracks().forEach((track) => {
      const alreadyAdded = existingSenders.some((s) => s.track === track);
      if (!alreadyAdded) {
        this.pc.addTrack(track, stream);
      }
    });
    this.applyVideoQuality(quality);
  }

  applyVideoQuality(quality: VideoQuality = '1080p'): void {
    const sender = this.pc.getSenders().find((s) => s.track?.kind === 'video');
    if (!sender) return;
    const preset = VIDEO_QUALITY_PRESETS[quality] || VIDEO_QUALITY_PRESETS['1080p'];
    const params = sender.getParameters();
    if (!params.encodings || params.encodings.length === 0) {
      params.encodings = [{}];
    }
    // Fixed resolution mode: enforce maintain-resolution unless set to auto
    params.degradationPreference = preset.degradationPreference;
    params.encodings[0].scaleResolutionDownBy = preset.scaleResolutionDownBy;
    params.encodings[0].maxBitrate = preset.maxBitrate;
    params.encodings[0].maxFramerate = preset.maxFramerate;
    sender.setParameters(params).catch((e) =>
      console.warn('[WebRTC] setParameters failed:', e)
    );
  }

  setVideoQuality(quality: VideoQuality): void {
    this.applyVideoQuality(quality);
  }

  async createOffer(): Promise<RTCSessionDescriptionInit | null> {
    if (this.isMakingOffer || this.pc.signalingState !== 'stable') {
      console.log(`[WebRTC] createOffer deferred/skipped (signalingState: ${this.pc.signalingState})`);
      return null;
    }

    try {
      this.isMakingOffer = true;
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      return offer;
    } catch (e) {
      console.warn('[WebRTC] createOffer failed:', e);
      return null;
    } finally {
      this.isMakingOffer = false;
    }
  }

  async handleOffer(sdp: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
    if (this.pc.signalingState !== 'stable') {
      console.log(`[WebRTC] handleOffer rollback from state: ${this.pc.signalingState}`);
      await Promise.all([
        this.pc.setLocalDescription({ type: 'rollback' }),
        this.pc.setRemoteDescription(sdp),
      ]);
    } else {
      await this.pc.setRemoteDescription(sdp);
    }
    await this.drainPendingCandidates();

    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    return answer;
  }

  async handleAnswer(sdp: RTCSessionDescriptionInit): Promise<void> {
    if (this.pc.signalingState !== 'have-local-offer') {
      console.warn(`[WebRTC] handleAnswer ignored in state: ${this.pc.signalingState}`);
      return;
    }
    await this.pc.setRemoteDescription(sdp);
    await this.drainPendingCandidates();
  }

  async addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.pc.remoteDescription) {
      this.pendingCandidates.push(candidate);
      return;
    }
    try {
      await this.pc.addIceCandidate(candidate);
    } catch (e) {
      console.warn('[WebRTC] addIceCandidate failed:', e);
    }
  }

  private async drainPendingCandidates(): Promise<void> {
    while (this.pendingCandidates.length > 0) {
      const cand = this.pendingCandidates.shift();
      if (cand) {
        try {
          await this.pc.addIceCandidate(cand);
        } catch (e) {
          console.warn('[WebRTC] drainPendingCandidate failed:', e);
        }
      }
    }
  }

  /** Start collecting stats every 2 seconds */
  startStats(onStats: (s: StreamStats) => void): void {
    this.statsTimer = setInterval(async () => {
      const stats = await this.collectStats();
      if (stats) onStats(stats);
    }, 2000);
  }

  stopStats(): void {
    if (this.statsTimer) {
      clearInterval(this.statsTimer);
      this.statsTimer = null;
    }
  }

  private async collectStats(): Promise<StreamStats | null> {
    const now = Date.now();
    const elapsed = (now - this.lastStatsTime) / 1000;
    this.lastStatsTime = now;

    let bitrate = 0;
    let frameRate = 0;
    let resolution = 'unknown';
    let packetLoss = 0;
    let roundTripTime = 0;
    let jitter = 0;
    let isRelay = false;

    const reports = await this.pc.getStats();

    reports.forEach((report) => {
      if (report.type === 'outbound-rtp' && report.kind === 'video') {
        const bytesSent = (report as RTCOutboundRtpStreamStats).bytesSent ?? 0;
        if (this.lastBytesSent > 0 && elapsed > 0) {
          bitrate = ((bytesSent - this.lastBytesSent) * 8) / 1000 / elapsed; // kbps
        }
        this.lastBytesSent = bytesSent;
        frameRate = (report as RTCOutboundRtpStreamStats).framesPerSecond ?? 0;
        const w = (report as RTCOutboundRtpStreamStats).frameWidth;
        const h = (report as RTCOutboundRtpStreamStats).frameHeight;
        if (w && h) resolution = `${w}x${h}`;
      }

      if (report.type === 'inbound-rtp' && report.kind === 'video') {
        const bytesReceived = (report as RTCInboundRtpStreamStats).bytesReceived ?? 0;
        if (this.lastBytesReceived > 0 && elapsed > 0) {
          bitrate = ((bytesReceived - this.lastBytesReceived) * 8) / 1000 / elapsed;
        }
        this.lastBytesReceived = bytesReceived;
        frameRate = (report as RTCInboundRtpStreamStats).framesPerSecond ?? 0;
        const w = (report as RTCInboundRtpStreamStats).frameWidth;
        const h = (report as RTCInboundRtpStreamStats).frameHeight;
        if (w && h) resolution = `${w}x${h}`;
        const lost = (report as RTCInboundRtpStreamStats).packetsLost ?? 0;
        const recv = (report as RTCInboundRtpStreamStats).packetsReceived ?? 1;
        packetLoss = lost / (lost + recv);
        jitter = ((report as RTCInboundRtpStreamStats).jitter ?? 0) * 1000;
      }

      if (report.type === 'candidate-pair' && (report as RTCIceCandidatePairStats).state === 'succeeded') {
        roundTripTime = ((report as RTCIceCandidatePairStats).currentRoundTripTime ?? 0) * 1000;
      }

      if (report.type === 'remote-candidate') {
        const candidateType = (report as RTCIceCandidate & { candidateType?: string }).type;
        if (candidateType === 'relay') isRelay = true;
      }
    });

    const stats: StreamStats = {
      bitrate: Math.round(bitrate),
      frameRate: Math.round(frameRate),
      resolution,
      packetLoss,
      roundTripTime: Math.round(roundTripTime),
      jitter: Math.round(jitter),
      connectionType: isRelay ? 'relay' : 'direct',
      qualityLabel: computeQualityLabel({ bitrate, packetLoss, roundTripTime }),
    };

    return stats;
  }

  get connectionState(): RTCPeerConnectionState {
    return this.pc.connectionState;
  }

  close(): void {
    this.stopStats();
    if (this.remoteStream) {
      this.remoteStream.getTracks().forEach((t) => t.stop());
      this.remoteStream = null;
    }
    this.pc.close();
  }
}
